import { computed, inject, provide, ref, toValue, watch } from 'vue'
import { refDebounced } from '@vueuse/core'
import { useField as veeUseField, useForm as veeUseForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { first, identity, keys, last } from 'lodash-es'
import JSON5 from 'json5'
import Ajv from 'ajv'
import { number as yupNumber, setLocale, string as yupString } from 'yup'
import { debounceDelayOnTyping, isFieldEmpty, isRowEmpty } from '../../shared/fns'
import { useUrlQueryUpdater } from '../../shared/use-url-query-updater'
import { isValidationError } from '../../shared/api'

const ajv = new Ajv({ verbose: true, strictTuples: false, discriminator: true, allErrors: true })

const emptyToNull = (value, originalValue) => originalValue === '' ? null : value

let l

export const string = () =>
  yupString().trim().transform(emptyToNull).nullable()

export const number = () =>
  yupNumber().transform(emptyToNull).nullable()

export const empty = () => schema => schema.oneOf([''], l('empty'))
export const required = () => schema => schema.required()
export const isTrue = () => schema => schema.oneOf([true], l('true')).required()
// export const isEmptyRequired = () => ({ is: isEmpty, then: required() })

const isValidJson = json => {
  try {
    JSON5.parse(json)
    return true
  } catch (error) {
    return false
  }
}

export const normalizeJson = value => {
  if (isFieldEmpty(value)) {
    return null
  }
  if (!isValidJson(value)) {
    return value
  }
  return JSON.stringify(JSON5.parse(value), null, 2)
}

export const schemaConformingJsonTest = (schema, errorsRef) => {
  const validate = ajv.compile(schema)
  return {
    name: 'schemaConformingJson',
    test: (value, context) => {
      if (errorsRef) {
        errorsRef.value = null
      }
      if (isFieldEmpty(value)) {
        return true
      }
      if (!isValidJson(value)) {
        return context.createError({ message: l('invalidJson') })
      }
      const conforming = validate(JSON5.parse(value))
      if (errorsRef) {
        errorsRef.value = validate.errors
      }
      if (!conforming) {
        return context.createError({ message: l('notSchemaCompliant') })
      }
      return true
    }
  }
}

export const overrideValidationMessages = vueI18n => {
  const prefix = 'validation'
  if (!l) {
    l = (code, ...rest) => () => vueI18n.global.t(`${prefix}.${code}`, ...rest)
  }
  setLocale({
    mixed: {
      default: l('invalid'),
      required: l('required'),
      notType: ({ type }) => {
        const messages = {
          default: l('invalid'),
          number: l('number')
        }
        return (messages[type] || messages.default)()
      }
    },
    number: {
      typeError: l('number'),
      min: ({ min }) => l('minNumber', [min])(),
      max: ({ max }) => l('maxNumber', [max])()
    },
    string: {
      matches: l('matches'),
      email: l('email'),
      min: ({ min }) => l('lengthMin', [min])()
    }
  })
}

export const useForm = opts => {
  const validationSchema = opts?.validationSchema
  const typedSchema = computed(() =>
    toValue(validationSchema) && toTypedSchema(toValue(validationSchema))
  )
  const updatedOpts = { ...opts, validationSchema: typedSchema }
  const form = veeUseForm(updatedOpts)
  const formFields = ref({})
  provide('validationSchema', validationSchema)
  provide('form', form)
  provide('formFields', formFields)

  const urlQueryUpdater = useUrlQueryUpdater()
  const validValues = ref(null)
  const debouncedValidValues = refDebounced(validValues, debounceDelayOnTyping)

  const updateValidValues = async () => {
    const result = await form.validate()
    if (result.valid) {
      validValues.value = { ...form.values }
    }
  }
  watch(form.values, async () => {
    await updateValidValues()
  }, { deep: true, immediate: true })
  watch([() => opts?.urlQuery, debouncedValidValues], async () => {
    if (opts?.urlQuery) {
      await urlQueryUpdater(toValue(debouncedValidValues))
    }
  }, { deep: true, immediate: true })

  const focusFirstError = () => {
    const firstErrorName = first(keys(form.errors.value))
    const scrollEl = document.querySelector(`[name="${firstErrorName}"]`)
    if (typeof scrollEl?.scrollIntoView === 'function') {
      scrollEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    }
    const focusEl = document.querySelector(`div[name="${firstErrorName}"] input, :not(div)[name="${firstErrorName}"]`)
    if (typeof focusEl?.focus === 'function') {
      focusEl.focus()
    }
  }

  const handleSubmit = cb => form.handleSubmit(cb, focusFirstError)

  const setLocalizedFieldError = (name, errorCode) => {
    form.setFieldError(name, l(...mapErrorCode(errorCode))())
  }

  const applyBackendErrors = (error, mapErrorPath = identity) => {
    if (isValidationError(error)) {
      error.response.data.errors.forEach(({ path, errorCode }) => {
        setLocalizedFieldError(mapErrorPath(path), errorCode)
      })
      focusFirstError()
      return true
    }
  }

  return {
    ...form,
    validValues,
    debouncedValidValues,
    focusFirstError,
    handleSubmit,
    setLocalizedFieldError,
    applyBackendErrors
  }
}

const mapErrorCode = errorCode => {
  if (errorCode.startsWith('sizeBetween_')) {
    const [min, max] = errorCode.split('_').slice(1)
    if (max > 2_000_000_000) {
      return ['lengthMin', [min]]
    }
  }
  return [errorCode]
}

export const useField = (name, noSuccessFeedback, opts) => {
  const field = veeUseField(name, undefined, opts)
  const validationSchema = inject('validationSchema', null)
  const hasValidation = !!toValue(validationSchema)?.fields?.[toValue(name)]
  const formFields = inject('formFields', null)
  if (formFields.value) {
    formFields.value[toValue(name)] = field
  }
  const validationClass = computed(() =>
    field.meta.touched
      ? field.meta.valid ? toValue(noSuccessFeedback) || !hasValidation ? '' : 'is-valid' : 'is-invalid'
      : ''
  )
  return { ...field, validationClass }
}

export const ignoreLastRow = value => {
  if (!Array.isArray(value)) {
    return value
  }
  return isRowEmpty(last(value)) ? value.slice(0, -1) : value
}

export const useAutoFilter = (values, setFieldValue) => (name, value) => {
  setFieldValue(name, values[name] === value ? null : value)
}
